import { supabase } from '../supabaseClient';
import type {
  EvaluationPlanAttachment,
  EvaluationPlanDraft,
  EvaluationStandardDraft,
} from '../types/evaluationPlan';
import {
  EMPTY_EVALUATION_LEVELS,
  clampLevelStepCount,
  parseEvaluationPlanAttachment,
} from '../types/evaluationPlan';
import type { EvaluationPlanCategoryScope } from '../utils/evaluationPlanCategorySelection';

export type WorkspaceEvaluationPlanAppliedSnapshot = {
  id: string;
  workspace_id: string;
  grade_id: string;
  source_plan_id: string | null;
  source_category_name: string;
  source_category_scope: EvaluationPlanCategoryScope;
  /** 개별 평가계획 출처(기본/팀장/사용자). 적용본 수정 시 user로 전환. */
  source_plan_scope: EvaluationPlanCategoryScope;
  plan: EvaluationPlanDraft;
  standards: EvaluationStandardDraft[];
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type WorkspaceEvaluationPlanAppliedUpsertItem = {
  sourcePlanId: string;
  sourceCategoryName: string;
  sourceCategoryScope: EvaluationPlanCategoryScope;
  sourcePlanScope: EvaluationPlanCategoryScope;
  plan: EvaluationPlanDraft;
  standards: EvaluationStandardDraft[];
  sortOrder?: number;
};

const APPLIED_SELECT_BASE = [
  'id',
  'workspace_id',
  'grade_id',
  'source_plan_id',
  'source_category_name',
  'source_category_scope',
  'evaluation_element_text',
  'evaluation_method_text',
  'unit_text',
  'lesson_evaluation_text',
  'level_a',
  'level_b',
  'level_c',
  'level_d',
  'level_e',
  'level_step_count',
  'criterion_rubric_attachment',
  'evaluation_sheet_attachment',
  'standards_snapshot',
  'sort_order',
  'created_at',
  'updated_at',
] as const;

const APPLIED_SELECT_BASE_WITHOUT_LEVEL_STEP = APPLIED_SELECT_BASE.filter((col) => col !== 'level_step_count');

const APPLIED_SELECT = ['source_plan_scope', ...APPLIED_SELECT_BASE].join(', ');
const APPLIED_SELECT_WITHOUT_PLAN_SCOPE = APPLIED_SELECT_BASE.join(', ');
const APPLIED_SELECT_WITHOUT_LEVEL_STEP = ['source_plan_scope', ...APPLIED_SELECT_BASE_WITHOUT_LEVEL_STEP].join(', ');
const APPLIED_SELECT_WITHOUT_PLAN_SCOPE_AND_LEVEL_STEP = APPLIED_SELECT_BASE_WITHOUT_LEVEL_STEP.join(', ');

function isMissingPlanScopeColumnError(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('source_plan_scope') && (m.includes('column') || m.includes('schema') || m.includes('does not exist'));
}

function isMissingLevelStepColumnError(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('level_step_count') && (m.includes('column') || m.includes('schema') || m.includes('does not exist'));
}

function resolveAppliedSelect(includePlanScope: boolean, includeLevelStep: boolean): string {
  if (includePlanScope && includeLevelStep) return APPLIED_SELECT;
  if (includePlanScope && !includeLevelStep) return APPLIED_SELECT_WITHOUT_LEVEL_STEP;
  if (!includePlanScope && includeLevelStep) return APPLIED_SELECT_WITHOUT_PLAN_SCOPE;
  return APPLIED_SELECT_WITHOUT_PLAN_SCOPE_AND_LEVEL_STEP;
}

function requireIds(workspaceId: string, gradeId: string): { ws: string; gid: string } {
  const ws = workspaceId.trim();
  const gid = gradeId.trim();
  if (!ws) throw new Error('워크스페이스가 없습니다.');
  if (!gid) throw new Error('클래스를 선택해 주세요.');
  return { ws, gid };
}

function normalizeAttachment(raw: unknown): EvaluationPlanAttachment | null {
  const parsed = parseEvaluationPlanAttachment(raw);
  return parsed;
}

function normalizeStandardsSnapshot(raw: unknown): EvaluationStandardDraft[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .map((row, idx) => ({
      id: typeof row.id === 'string' && row.id.trim() ? row.id.trim() : `snap-std-${idx}`,
      track_text: typeof row.track_text === 'string' ? row.track_text : '',
      area_text: typeof row.area_text === 'string' ? row.area_text : '',
      achievement_code: typeof row.achievement_code === 'string' ? row.achievement_code : '',
      achievement_text: typeof row.achievement_text === 'string' ? row.achievement_text : '',
      level_a: typeof row.level_a === 'string' ? row.level_a : '',
      level_b: typeof row.level_b === 'string' ? row.level_b : '',
      level_c: typeof row.level_c === 'string' ? row.level_c : '',
      level_d: typeof row.level_d === 'string' ? row.level_d : '',
      level_e: typeof row.level_e === 'string' ? row.level_e : '',
      sort_order: typeof row.sort_order === 'number' ? row.sort_order : idx,
      category_name: typeof row.category_name === 'string' ? row.category_name : undefined,
    }));
}

function mapRow(row: Record<string, unknown>): WorkspaceEvaluationPlanAppliedSnapshot {
  const scopeRaw = typeof row.source_category_scope === 'string' ? row.source_category_scope : 'user';
  const source_category_scope: EvaluationPlanCategoryScope =
    scopeRaw === 'admin' || scopeRaw === 'team' || scopeRaw === 'user' ? scopeRaw : 'user';
  const planScopeRaw = typeof row.source_plan_scope === 'string' ? row.source_plan_scope : source_category_scope;
  const source_plan_scope: EvaluationPlanCategoryScope =
    planScopeRaw === 'admin' || planScopeRaw === 'team' || planScopeRaw === 'user'
      ? planScopeRaw
      : source_category_scope;
  const id = String(row.id);
  const standards = normalizeStandardsSnapshot(row.standards_snapshot);
  const plan: EvaluationPlanDraft = {
    id,
    grade_id: typeof row.grade_id === 'string' ? row.grade_id : null,
    category_name: typeof row.source_category_name === 'string' ? row.source_category_name : '',
    standard_ids: standards.map((s) => s.id),
    evaluation_element_text: typeof row.evaluation_element_text === 'string' ? row.evaluation_element_text : '',
    evaluation_method_text: typeof row.evaluation_method_text === 'string' ? row.evaluation_method_text : '',
    unit_text: typeof row.unit_text === 'string' ? row.unit_text : '',
    lesson_evaluation_text: typeof row.lesson_evaluation_text === 'string' ? row.lesson_evaluation_text : '',
    criterion_rubric_attachment: normalizeAttachment(row.criterion_rubric_attachment),
    evaluation_sheet_attachment: normalizeAttachment(row.evaluation_sheet_attachment),
    level_step_count: clampLevelStepCount(row.level_step_count),
    sort_order: typeof row.sort_order === 'number' ? row.sort_order : 0,
    level_a: typeof row.level_a === 'string' ? row.level_a : EMPTY_EVALUATION_LEVELS.level_a,
    level_b: typeof row.level_b === 'string' ? row.level_b : EMPTY_EVALUATION_LEVELS.level_b,
    level_c: typeof row.level_c === 'string' ? row.level_c : EMPTY_EVALUATION_LEVELS.level_c,
    level_d: typeof row.level_d === 'string' ? row.level_d : EMPTY_EVALUATION_LEVELS.level_d,
    level_e: typeof row.level_e === 'string' ? row.level_e : EMPTY_EVALUATION_LEVELS.level_e,
  };
  return {
    id,
    workspace_id: String(row.workspace_id),
    grade_id: String(row.grade_id),
    source_plan_id: typeof row.source_plan_id === 'string' ? row.source_plan_id : null,
    source_category_name: typeof row.source_category_name === 'string' ? row.source_category_name : '',
    source_category_scope,
    source_plan_scope,
    plan,
    standards,
    sort_order: plan.sort_order,
    created_at: typeof row.created_at === 'string' ? row.created_at : '',
    updated_at: typeof row.updated_at === 'string' ? row.updated_at : '',
  };
}

export async function getWorkspaceEvaluationPlanApplied(
  workspaceId: string,
  gradeId: string
): Promise<WorkspaceEvaluationPlanAppliedSnapshot[]> {
  const { ws, gid } = requireIds(workspaceId, gradeId);
  const run = async (includePlanScope: boolean, includeLevelStep: boolean) =>
    supabase
      .from('workspace_evaluation_plan_applied')
      .select(resolveAppliedSelect(includePlanScope, includeLevelStep))
      .eq('workspace_id', ws)
      .eq('grade_id', gid)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

  let includePlanScope = true;
  let includeLevelStep = true;
  let { data, error } = await run(includePlanScope, includeLevelStep);
  if (error && isMissingPlanScopeColumnError(error.message)) {
    includePlanScope = false;
    ({ data, error } = await run(includePlanScope, includeLevelStep));
  }
  if (error && isMissingLevelStepColumnError(error.message)) {
    includeLevelStep = false;
    ({ data, error } = await run(includePlanScope, includeLevelStep));
  }
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRow);
}

/** 선택 목록으로 클래스 적용 평가계획을 통째로 교체(독립 스냅샷 저장). */
export async function replaceWorkspaceEvaluationPlanApplied(
  workspaceId: string,
  gradeId: string,
  items: WorkspaceEvaluationPlanAppliedUpsertItem[]
): Promise<WorkspaceEvaluationPlanAppliedSnapshot[]> {
  const { ws, gid } = requireIds(workspaceId, gradeId);
  const now = new Date().toISOString();

  const { error: deleteError } = await supabase
    .from('workspace_evaluation_plan_applied')
    .delete()
    .eq('workspace_id', ws)
    .eq('grade_id', gid);
  if (deleteError) throw new Error(deleteError.message);

  if (!items.length) return [];

  const toPayload = (includePlanScope: boolean, includeLevelStep: boolean) =>
    items.map((item, idx) => {
      const plan = item.plan;
      const standards = item.standards.map((s, sIdx) => ({
        id: s.id,
        track_text: s.track_text ?? '',
        area_text: s.area_text ?? '',
        achievement_code: s.achievement_code ?? '',
        achievement_text: s.achievement_text ?? '',
        level_a: s.level_a ?? '',
        level_b: s.level_b ?? '',
        level_c: s.level_c ?? '',
        level_d: s.level_d ?? '',
        level_e: s.level_e ?? '',
        sort_order: s.sort_order ?? sIdx,
        category_name: s.category_name ?? item.sourceCategoryName,
      }));
      const row: Record<string, unknown> = {
        workspace_id: ws,
        grade_id: gid,
        source_plan_id: item.sourcePlanId.trim() || null,
        source_category_name: item.sourceCategoryName.trim(),
        source_category_scope: item.sourceCategoryScope,
        evaluation_element_text: plan.evaluation_element_text ?? '',
        evaluation_method_text: plan.evaluation_method_text ?? '',
        unit_text: plan.unit_text ?? '',
        lesson_evaluation_text: plan.lesson_evaluation_text ?? '',
        level_a: plan.level_a ?? '',
        level_b: plan.level_b ?? '',
        level_c: plan.level_c ?? '',
        level_d: plan.level_d ?? '',
        level_e: plan.level_e ?? '',
        criterion_rubric_attachment: plan.criterion_rubric_attachment ?? null,
        evaluation_sheet_attachment: plan.evaluation_sheet_attachment ?? null,
        standards_snapshot: standards,
        sort_order: typeof item.sortOrder === 'number' ? item.sortOrder : idx,
        updated_at: now,
      };
      if (includeLevelStep) {
        row.level_step_count = clampLevelStepCount(plan.level_step_count);
      }
      if (includePlanScope) {
        row.source_plan_scope = item.sourcePlanScope;
      }
      return row;
    });

  const insertWith = async (includePlanScope: boolean, includeLevelStep: boolean) =>
    supabase
      .from('workspace_evaluation_plan_applied')
      .insert(toPayload(includePlanScope, includeLevelStep))
      .select(resolveAppliedSelect(includePlanScope, includeLevelStep))
      .order('sort_order', { ascending: true });

  let includePlanScope = true;
  let includeLevelStep = true;
  let { data, error } = await insertWith(includePlanScope, includeLevelStep);
  if (error && isMissingPlanScopeColumnError(error.message)) {
    includePlanScope = false;
    ({ data, error } = await insertWith(includePlanScope, includeLevelStep));
  }
  if (error && isMissingLevelStepColumnError(error.message)) {
    includeLevelStep = false;
    ({ data, error } = await insertWith(includePlanScope, includeLevelStep));
  }
  if (error) throw new Error(error.message);
  return ((data ?? []) as unknown as Record<string, unknown>[]).map(mapRow);
}
